import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from 'firebase/storage';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as limitQuery,
  startAfter,
  endBefore,
  endAt,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  limitToLast,
} from 'firebase/firestore';
import {
  getAuth,
} from 'firebase/auth';
import {
  getFunctions,
  httpsCallable,
} from 'firebase/functions';

export const getActiveStreams = async ({ state, commit, rootState }, orgId) => {
  const db = getFirestore();
  const auth = getAuth();
  const { currentUser } = auth;
  const userInfo = await getDoc(doc(db, 'users', currentUser.uid));
  let streamsQuery;
  if (rootState.auth.claims?.superAdmin || rootState.auth.claims?.eyeStaff) {
    streamsQuery = query(
      collection(db, 'streams'),
      where('organizationId', '==', orgId),
    );
  } else if (rootState.auth.claims?.orgAdmin) {
    const organizationInfo = await getDoc(userInfo.data().organization);
    const organizationId = organizationInfo.id;
    streamsQuery = query(
      collection(db, 'streams'),
      where('organizationId', '==', organizationId),
    );
  } else {
    streamsQuery = query(
      collection(db, 'streams'),
      where('userId', '==', currentUser.uid),
    );
  }
  if (state.unsubStreams) {
    state.unsubStreams();
  }
  state.streams = [];
  state.displayedVideos = [];
  state.unsubStreams = onSnapshot(streamsQuery, (snapshot) => {
    const streams = [...state.streams];
    const activeStreams = [...state.displayedVideos];

    const changes = snapshot.docChanges();
    const addedStreamIds = [];

    changes.forEach((c) => {
      if (c.type === 'added' && c.doc.data().recordingType === 'supervisor-call') {
        const streamData = {
          ...c.doc.data(),
          isLive: rootState.userPreferences.autoPlay,
          id: c.doc.id,
          position: '',
        };

        if (rootState.userPreferences.autoPlay) {
          activeStreams.push({ ...streamData, isLive: true });
        }

        state.isNewStream = true;
        streams.push(streamData);
        addedStreamIds.push(c.doc.id);
      } else if (c.type === 'modified') {
        const streamIndex = streams.findIndex((s) => s.id === c.doc.id);

        if (streamIndex !== -1) {
          streams[streamIndex] = {
            ...c.doc.data(),
            isLive: streams[streamIndex].isLive,
            id: c.doc.id,
            position: streams[streamIndex].position || '',
          };
        }
      } else if (c.type === 'removed') {
        const streamIndex = streams.findIndex((s) => s.id === c.doc.id);
        const activeStreamIndex = activeStreams.findIndex((s) => s.id === c.doc.id);

        if (activeStreamIndex !== -1) {
          activeStreams.splice(activeStreamIndex, 1);
        }

        if (streamIndex !== -1) {
          streams.splice(streamIndex, 1);
        }
        state.isNewStream = false;
      }
    });

    commit('SET_STATE', { key: 'streams', value: streams });
    commit('SET_STATE', { key: 'displayedVideos', value: activeStreams });

    if (!state.isInitialized) commit('INITIALIZE');

    // Fetch device positions asynchronously and update streams afterward
    if (addedStreamIds.length > 0) {
      Promise.all(
        changes
          .filter((c) => c.type === 'added' && addedStreamIds.includes(c.doc.id))
          .map(async (c) => {
            try {
              let deviceData;
              if (c.doc.data().streamType === 'WebRTC') {
                const userRef = doc(db, 'users', c.doc.data().userId);
                const devicesRef = collection(db, 'devices');
                const deviceQuery = query(devicesRef, where('owner', '==', userRef));
                const snapShotDevice = await getDocs(deviceQuery);
                deviceData = snapShotDevice.docs[0]?.data();
              } else {
                const snapShotDevice = await getDoc(doc(db, 'devices', c.doc.id));
                deviceData = snapShotDevice.data();
              }
              return { id: c.doc.id, position: deviceData?.position || '' };
            } catch {
              return { id: c.doc.id, position: '' };
            }
          }),
      ).then((deviceResults) => {
        const updatedStreams = [...state.streams];
        const updatedActive = [...state.displayedVideos];
        let changed = false;

        deviceResults.forEach(({ id, position }) => {
          if (!position) return;
          const si = updatedStreams.findIndex((s) => s.id === id);
          if (si !== -1 && updatedStreams[si].position !== position) {
            updatedStreams[si] = { ...updatedStreams[si], position };
            changed = true;
          }
          const ai = updatedActive.findIndex((s) => s.id === id);
          if (ai !== -1 && updatedActive[ai].position !== position) {
            updatedActive[ai] = { ...updatedActive[ai], position };
            changed = true;
          }
        });

        if (changed) {
          commit('SET_STATE', { key: 'streams', value: updatedStreams });
          commit('SET_STATE', { key: 'displayedVideos', value: updatedActive });
        }
      });
    }
  });
};

export const getVideos = ({ state, commit }, {
  videoQuery, paged, filter, limit,
}) => {
  if (state.unsubVideos) {
    state.unsubVideos();
  }
  state.recordedVideos = [];
  state.unsubVideos = onSnapshot(videoQuery, (snapshot) => {
    let videos = [...state.recordedVideos];

    const changes = snapshot.docChanges();
    changes.forEach((c) => {
      if (c.type === 'added') {
        const vid = c.doc.data();
        const video = { ...vid, id: c.doc.id, ownerName: vid.title };
        videos.splice(c.newIndex, 0, video);
      } else if (c.type === 'modified') {
        const videoIndex = videos.findIndex((s) => s.id === c.doc.id);

        if (videoIndex !== -1) {
          const vid = c.doc.data();
          videos[videoIndex] = {
            ...vid,
            id: c.doc.id,
            ownerName: vid.title,
          };
        }
      } else if (c.type === 'removed') {
        const streamIndex = videos.findIndex((s) => s.id === c.doc.id);

        if (streamIndex !== -1) {
          videos.splice(streamIndex, 1);
        }
      }
    });
    commit('SET_STATE', { key: 'recordedVideos', value: videos });

    if (paged && snapshot.docs.length > 0) {
      commit('SET_STATE', { key: 'firstVisibleVideo', value: snapshot.docs[0] });
      commit('SET_STATE', { key: 'lastVisibleVideo', value: snapshot.docs[snapshot.docs.length - 1] });
    }

    if (!state.isRecordedVideosFetched) {
      commit('SET_STATE', { key: 'isRecordedVideosFetched', value: true });
    }
  });
};

export const getVideosByOrg = async ({ state, dispatch },
  {
    status, orgId, limit = 10, filter,
  }) => {
  const db = getFirestore();
  const videosRef = collection(db, 'videos');
  let baseQuery = [];

  baseQuery = [
    where('organizationId', '==', orgId),
    where('deleted', '==', false),
    orderBy('dateAdded', 'desc'),
  ];

  if (filter !== 'all') {
    baseQuery.push(where('recordingType', '==', filter));
  }

  let videosQuery;
  if (status === 'next') {
    videosQuery = query(videosRef, ...baseQuery, startAfter(state.lastVisibleVideo), limitQuery(limit));
  } else if (status === 'prev') {
    videosQuery = query(videosRef, ...baseQuery, endBefore(state.firstVisibleVideo), limitToLast(limit));
  } else if (status === 'back') {
    videosQuery = query(videosRef, ...baseQuery, endAt(state.lastVisibleVideo), limitToLast(limit));
  } else {
    state.page = 1;
    videosQuery = query(videosRef, ...baseQuery, limitQuery(limit));
  }
  dispatch('getVideos', {
    videoQuery: videosQuery, paged: true, filter, limit,
  });
  dispatch('getVideoTotalSize', {
    id: orgId, pageSize: limit, isOrgVideo: true, filter,
  });
};

export const getVideoTotalSize = async ({ commit },
  {
    id, pageSize, isOrgVideo, isFavorite, filter,
  }) => {
  const db = getFirestore();
  const videosRef = collection(db, 'videos');
  const conditions = [where('deleted', '==', false)];

  if (isOrgVideo) {
    conditions.push(where('organizationId', '==', id));
  } else {
    conditions.push(where('userId', '==', id));
  }

  if (filter !== 'all') {
    conditions.push(where('recordingType', '==', filter));
  }
  if (isFavorite) {
    conditions.push(where('isFavorite', '==', true));
  }

  const videoQuery = query(videosRef, ...conditions);
  const snapshot = await getDocs(videoQuery);
  const pages = snapshot.size % pageSize === 0 ? (snapshot.size / pageSize) : Math.floor(snapshot.size / pageSize) + 1;
  commit('SET_PAGES_VALUE', pages);
};

export const getVideosByUser = async ({ state, dispatch }, {
  id, status, limit, filter = 'all', favorite,
}) => {
  const db = getFirestore();
  const videosRef = collection(db, 'videos');
  const conditions = [
    where('userId', '==', id),
    where('deleted', '==', false),
    orderBy('dateAdded', 'desc'),
  ];

  if (filter !== 'all') {
    conditions.push(where('recordingType', '==', filter));
  }
  if (favorite) {
    conditions.push(where('isFavorite', '==', true));
  }

  let userVideoQuery;
  if (status === 'next') {
    userVideoQuery = query(videosRef, ...conditions, startAfter(state.lastVisibleVideo), limitQuery(limit));
  } else if (status === 'prev') {
    userVideoQuery = query(videosRef, ...conditions, endBefore(state.firstVisibleVideo), limitToLast(limit));
  } else if (status === 'back') {
    userVideoQuery = query(videosRef, ...conditions, endAt(state.lastVisibleVideo), limitToLast(limit));
  } else {
    state.page = 1;
    userVideoQuery = query(videosRef, ...conditions, limitQuery(limit));
  }
  dispatch('getVideos', {
    videoQuery: userVideoQuery, paged: true, filter, limit,
  });
  dispatch('getVideoTotalSize',
    {
      id, pageSize: limit, isOrgVideo: false, isFavorite: favorite, filter,
    });
};

export const deleteVideo = async ({ commit }, { payload }) => {
  const db = getFirestore();
  commit('SET_STATE', { key: 'isLoading', value: true });

  try {
    const videoRef = doc(db, 'videos', payload.id);
    await updateDoc(videoRef, {
      deleted: serverTimestamp(),
    });
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
};

export const resetState = ({ commit }) => {
  commit('RESET_STATE');
};

export const generatePdf = async ({ commit }, { payload }) => {
  commit('SET_STATE', { key: 'isLoading', value: true });
  let resp = false;
  try {
    const functions = getFunctions();
    const generateFile = httpsCallable(functions, 'generatePdf');
    const result = await generateFile({
      vidId: payload.id,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: payload.lang,
    });
    resp = result.data;
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
  return resp;
};

export const getTwilioAccessToken = async ({ commit }, { payload }) => {
  commit('SET_STATE', { key: 'isLoading', value: true });
  try {
    const functions = getFunctions();
    const getAccessToken = httpsCallable(functions, 'getTwilioAccessToken');
    const { data: token } = await getAccessToken(payload);

    if (!token) {
      commit('SET_STATE', { key: 'accessToken', value: false });
      commit('SET_STATE', {
        key: 'error',
        value: 'Missing Twilio access token in callable response',
      });
      return null;
    }

    commit('SET_STATE', { key: 'accessToken', value: token });
    return token;
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
    return null;
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
};

export const getVideoLink = async ({ commit }, { payload }) => {
  commit('SET_STATE', { key: 'isLoading', value: true });
  let resp = false;
  try {
    const functions = getFunctions();
    const getLink = httpsCallable(functions, 'getVideoLink');
    const result = await getLink(payload.id);
    resp = result.data;
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
  return resp;
};

export const getThumbnailLink = async ({ commit }, { payload }) => {
  commit('SET_STATE', { key: 'isLoading', value: true });
  let filePath = payload.thumbnailLink;
  const storage = getStorage();
  const thumbnailRef = storageRef(storage, filePath);
  try {
    filePath = await getDownloadURL(thumbnailRef);
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
  return filePath;
};
